import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { DataTable } from '@/components/DataTable';
import {
  Card,
  ErrorState,
  Loading,
  PageHeader,
  Select,
  StatCard,
} from '@/components/ui';
import { WINDOWS, formatDateTime, formatMoney, formatNumber, titleCase } from '@/lib/format';
import { useShowsUsageCosts } from '@/lib/license';

interface UsageSummary {
  window_days: number;
  minutes: number;
  calls: number;
  charges: number;
  qualified_leads: number;
  transfers: number;
  by_kind: Array<{ kind: string; quantity: number; charges: number }>;
}

interface UsageEvent {
  id: number;
  call_id: string | null;
  kind: string;
  model: string | null;
  quantity: string;
  unit: string;
  client_price: string;
  currency: string;
  occurred_at: string;
  /** Only present when the tenant has expose_provider_costs enabled. */
  actual_cost?: string;
  unit_cost?: string;
}

const KIND_LABELS: Record<string, string> = {
  LLM_INPUT_TOKENS: 'AI input tokens',
  LLM_OUTPUT_TOKENS: 'AI output tokens',
  STT_SECONDS: 'Speech recognition',
  TTS_CHARACTERS: 'Speech synthesis',
  TELEPHONY_MINUTES: 'Telephony minutes',
};

export default function Usage() {
  const [days, setDays] = useState(30);
  // Quantities are always the client's to see; prices only on per-minute
  // billing. The API removes them too -- this only keeps the layout honest.
  const showsCosts = useShowsUsageCosts();

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['usage', days],
    queryFn: () => api.get<UsageSummary>('/usage', { days }),
  });

  return (
    <div className="w-full">
      <PageHeader
        title="Usage"
        description="What your bots consumed, and what it costs you."
        actions={
          <Select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="w-auto"
          >
            {WINDOWS.map((w) => (
              <option key={w.days} value={w.days}>
                Last {w.label}
              </option>
            ))}
          </Select>
        }
      />

      {isPending ? (
        <Loading />
      ) : error ? (
        <ErrorState error={error} retry={() => refetch()} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <StatCard label="Calls" value={formatNumber(data.calls)} />
            <StatCard label="Minutes" value={formatNumber(data.minutes)} />
            <StatCard
              label="Qualified leads"
              value={formatNumber(data.qualified_leads)}
              tone="good"
            />
            <StatCard label="Transfers" value={formatNumber(data.transfers)} />
            {showsCosts && <StatCard label="Charges" value={formatMoney(data.charges)} />}
          </div>

          <Card title="By category" className="mt-6" padded={false}>
            <table className="min-w-full divide-y divide-ink-200 text-sm dark:divide-ink-800">
              <thead className="bg-ink-50/70 dark:bg-ink-950/40">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
                    Category
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-ink-500">
                    Quantity
                  </th>
                  {showsCosts && (
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-ink-500">
                      Charges
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100 dark:divide-ink-800/70">
                {data.by_kind.map((row) => (
                  <tr key={row.kind}>
                    <td className="px-4 py-2.5">{KIND_LABELS[row.kind] ?? titleCase(row.kind)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {formatNumber(Math.round(row.quantity))}
                    </td>
                    {showsCosts && (
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        {formatMoney(row.charges)}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      <div className="mt-6">
        <DataTable<UsageEvent>
          title="Usage events"
          description="Every metered service call."
          queryKey="usage-events"
          path="/usage/events"
          searchable={false}
          pageSize={50}
          emptyTitle="No usage recorded yet"
          filters={[
            {
              key: 'kind',
              label: 'Category',
              options: Object.keys(KIND_LABELS).map((value) => ({
                value,
                label: KIND_LABELS[value],
              })),
            },
          ]}
          columns={[
            {
              key: 'occurred_at',
              header: 'When',
              render: (event) => (
                <span className="whitespace-nowrap text-xs text-ink-500">
                  {formatDateTime(event.occurred_at)}
                </span>
              ),
            },
            {
              key: 'kind',
              header: 'Category',
              render: (event) => KIND_LABELS[event.kind] ?? titleCase(event.kind),
            },
            {
              key: 'model',
              // The service, not the vendor behind it. The server brands this;
              // the column is no longer a machine identifier, so it is set in
              // ordinary text rather than mono.
              header: 'Service',
              render: (event) => (
                <span className="text-xs text-ink-500">{event.model ?? '—'}</span>
              ),
            },
            {
              key: 'quantity',
              header: 'Quantity',
              numeric: true,
              render: (event) => `${formatNumber(Math.round(Number(event.quantity)))} ${event.unit}`,
            },
            ...(showsCosts
              ? [
                  {
                    key: 'charge',
                    header: 'Charge',
                    numeric: true,
                    render: (event: UsageEvent) => formatMoney(event.client_price, event.currency),
                  },
                ]
              : []),
          ]}
        />
      </div>
    </div>
  );
}
